/**
 * src/api/riskAiAssessApi.js
 *
 * Client for risk-ai-assess (RAI-04 endpoint, RAI-05 UI; design
 * docs/risk-ai-assessment-design.md §2-§4, contract
 * function-contracts/risk-ai-assess.json).
 *
 * Every read is live: the server re-derives engine statuses, downgrades and
 * the decision on each call (RISK-B-008), so nothing here caches an
 * assessment. A refused call throws the transport's ApiError, whose
 * `responseBody` carries the server's own answer (FIELD_CONFLICT with the field and
 * both values, RISK_BEING_CREATED with the risk id, …) — callers render that,
 * never a guess. A 403 is thrown before the body is kept, so its code is the
 * error message ('FEATURE_NOT_IN_PLAN', 'Forbidden').
 */

import { azurePost, getTenantId } from './azureClient';

const PATH = '/api/risk-ai-assess';

function call(action, body = {}) {
  return azurePost(`${PATH}?action=${encodeURIComponent(action)}`, {
    tenantId: getTenantId(),
    ...body,
  });
}

/** Run a new assessment. item = { description, scope: { assetIds?, process?, riskId? } } */
export async function startRiskAiAssessment(item) {
  const res = await call('start', { item });
  return res.assessment;
}

/**
 * Answer asked questions; the server re-runs the model over every recorded
 * answer. answers = [{ index, answer, supersedesEventId? }].
 * → { assessment, rerun, conflicts? }
 */
export function answerRiskAiQuestions(assessmentId, answers) {
  return call('answer', { assessmentId, answers });
}

/** Re-run when the estimate shown does not include every answer. → { assessment, rerun } */
export function rerunRiskAiAssessment(assessmentId) {
  return call('rerun', { assessmentId });
}

/** The risk owner's attributed relevance act on one candidate. */
export async function confirmRiskAiRelevance(
  assessmentId,
  { controlId, decision, supersedesEventId }
) {
  const res = await call('confirm-relevance', {
    assessmentId,
    controlId,
    decision,
    supersedesEventId: supersedesEventId || null,
  });
  return res.assessment;
}

/** An attributed dismissal; reason is required. */
export async function dismissRiskAiAssessment(
  assessmentId,
  { reason, reasonKind, supersedesEventId }
) {
  const res = await call('dismiss', {
    assessmentId,
    reason,
    reasonKind,
    supersedesEventId: supersedesEventId || null,
  });
  return res.assessment;
}

/**
 * Create the draft risk from this assessment (server-side, through the one
 * create path). risk = { name, scenario?, rootCause?, consequence?, riskOwnerId? }
 * → { assessment, risk, converged }
 */
export function createRiskFromAiAssessment(assessmentId, risk) {
  return call('create-risk', { assessmentId, risk });
}

/** One assessment, re-derived now. A purged one returns { purged: true, run: null, evidence }. */
export async function getRiskAiAssessment(assessmentId) {
  const res = await call('get', { assessmentId });
  return res.assessment;
}

/**
 * Every assessment that is not purged, newest first → { assessments, undecided }.
 * With riskId: the assessments about that risk (linked to it or started from
 * it), purged ones included as tombstones with their `evidence` dates.
 */
export function listRiskAiAssessments({ riskId } = {}) {
  return call('list', riskId ? { riskId } : {});
}

/** The server's body on a refused call, or {}. */
export function riskAiErrorBody(err) {
  return (err && err.responseBody) || {};
}

/** The server's error code on a refused call ('FIELD_CONFLICT', 'RISK_BEING_CREATED', …). */
export function riskAiErrorCode(err) {
  if (!err) return null;
  const body = riskAiErrorBody(err);
  return (typeof body.error === 'string' && body.error) || err.message || null;
}
